/**
 * Staff-facing explanations of recruitment checks.
 *
 * Never turn a backend requirement into a hiring decision. These descriptions
 * are display-only; all verification and deployment rules remain server-owned.
 * Avoid asking staff to interpret codes, internal gate names or raw statuses.
 */
const normalize = (value) => String(value ?? '').replace(/[_-]/g, ' ').replace(/\s+/g, ' ').trim();
const friendlyFallback = (value) => {
  const clean = normalize(value)
    .replace(/^(missing|blocked|pending|failed|not verified|requires action)\s*:\s*/i, '')
    .replace(/\b(sign off|sign-off)\b/gi, 'final review')
    .replace(/\bRTW\b/gi, 'Right to Work');
  return clean.length <= 110 ? clean : `${clean.slice(0, 107)}…`;
};

export function explainRecruitmentItem(raw) {
  const item = typeof raw === 'string' ? { label: raw } : (raw || {});
  const original = normalize([item.label, item.reason, item.fixHint, item.fix_hint, item.message].filter(Boolean).join(' '));
  const key = normalize(item.requirementKey || item.requirement_key || item.row_key || item.key || item.id);
  const text = `${key} ${original}`.toLowerCase();

  const guide = (title, owner, step, note = '') => ({ title, owner, step, note });
  if (/health questionnaire|staff health|health declaration|work health/.test(text)) {
    const deferred = /not yet|after interview|until post interview|locked|not due|conditional offer|provisional offer|available after|work health locked/.test(text);
    return deferred
      ? guide('Health questionnaire — not due yet', 'Care provider',
        'Continue the available recruitment checks. Once the provider records the appropriate selection or offer stage, the applicant can complete the confidential health questionnaire.',
        'Do not collect medical history early or treat this as an applicant mistake.')
      : guide('Health questionnaire needs attention', 'Applicant and authorised health reviewer',
        'Check whether the applicant has submitted the private questionnaire. An authorised reviewer must record the appropriate outcome.',
        'Recruitment staff should see the result, not unnecessary medical details.');
  }

  if (/interview appointment|interview not scheduled|book.*interview|interview slot/.test(text)) {
    return guide('Interview needs booking', 'Recruitment team',
      'Agree a date with the applicant and book it. Other available checks can continue while you arrange it.');
  }
  if (/interview record|interview assessment|pass.fail outcome|interview completed|interview not yet/.test(text)) {
    return guide('Interview outcome needs recording', 'Interviewer or recruitment manager',
      'After the interview, record the assessment and decision in CareDock. Do not mark the interview passed before it takes place.');
  }
  if (/interview/.test(text)) {
    return guide('Interview needs attention', 'Recruitment team',
      'Open the Interview section to check the appointment and assessment. Other paperwork can continue.');
  }

  if (/right to work|\brtw\b|share code|work restriction/.test(text)) {
    return guide('Right to Work check', 'Recruitment team',
      'Check the applicant’s permitted evidence using the official process, record the result and any restrictions.',
      'A submitted share code is not the same as a completed check.');
  }
  if (/\bdbs\b|disclosure and barring|criminal record certificate/.test(text)) {
    return guide('DBS check', 'Recruitment team',
      'Open the DBS record. Check whether an application, result or certificate is already on file, then complete the required review.',
      'Do not mark the applicant cleared just because an application has started.');
  }
  if (/proof of address|address document|address verification/.test(text)) {
    return guide('Proof of address', 'Applicant, then recruitment team',
      'Check whether the applicant has supplied acceptable address evidence. Request it only if no usable evidence is on file, then review it.');
  }
  if (/identity|passport|photo id|id document|identification/.test(text)) {
    return guide('Identity documents', 'Applicant, then recruitment team',
      'Open the ID record, inspect any documents already supplied, and request an acceptable document only if needed.');
  }
  if (/reference|referee/.test(text)) {
    return guide('Employment reference', 'Recruitment team',
      'Check the referee details, send or follow up on the request, and review the response. The applicant may help with missing contact details.');
  }
  if (/employment review|employment history|work history|\bgap\b/.test(text)) {
    return guide('Work history review', 'Applicant and recruitment manager',
      'Open the employment history. Confirm saved jobs and dates, resolve any unexplained gaps, and complete the required reviews.',
      'Do not invent dates or approve unexplained gaps just to clear a warning.');
  }
  if (/\bcv\b|resume|résumé/.test(text)) {
    return guide('CV review', 'Recruitment team',
      'Open the CV and compare it with the applicant’s recorded work history. Request clarification only where the details do not agree.');
  }
  if (/declaration|consent/.test(text)) {
    return guide('Applicant declarations', 'Recruitment team',
      'Open the submitted declarations, review the applicant’s answers and record your decision. Do not answer or consent on their behalf.');
  }
  if (/training|certificate|qualification|induction/.test(text)) {
    return guide('Training or certificate check', 'Applicant and training reviewer',
      'Check whether evidence is already on file. If a required course or certificate is outstanding, show the applicant the next step.',
      'A training record does not always mean the certificate file is available.');
  }
  if (/offer|hiring decision/.test(text)) {
    return guide('Employer’s decision needs recording', 'Care provider',
      'The employer decides whether to offer the role. Record the actual decision in CareDock; CareDock does not make or send an offer by itself.');
  }
  if (/sign off|approval|final review|ready for/.test(text)) {
    return guide('Manager’s final review', 'Authorised manager',
      'Check the supporting records and finish the required approvals. Do not bypass missing evidence or review steps.');
  }
  if (/form|personal information|hmrc|emergency contact/.test(text)) {
    return guide('Applicant form needs attention', 'Applicant, then recruitment team',
      'Check whether the form is available and already submitted. Ask the applicant to complete any unanswered sections, then review them.');
  }

  return guide(friendlyFallback(item.label || item.reason || 'A check needs attention'),
    'Recruitment team', 'Open the linked check, read the supporting record, and identify what is genuinely outstanding.',
    'If the file or reason is unclear, ask a manager rather than marking it complete without evidence.');
}
